import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  ArrowRight, Building2, Briefcase, Crown, FileCode2, Send, Inbox, Undo2, FileDown, LineChart, Users, ShieldCheck, Download, Hash, Warehouse, Truck, Receipt, Printer,
} from "lucide-react";
import { useAuth, homeFor } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { RequestForm } from "@/components/RequestForm";
import { HeroSlideshow } from "@/components/HeroSlideshow";
import { ForfettariPromo } from "@/components/ForfettariPromo";
import { PackagesSection } from "@/components/PackagesSection";
import { FUNZIONI } from "@/pages/Funzione";
import { BrandMark } from "@/components/LogoUploader";
import { useBranding } from "@/lib/branding";

const FEATURES = [
  [FileCode2, "XML FatturaPA 1.2.2", "Generazione conforme FPR12/FPA12, validata sullo schema ufficiale dell'Agenzia delle Entrate."],
  [Send, "Invio allo SdI", "Predisposto per la trasmissione tramite intermediario accreditato, attivabile su richiesta per ogni azienda."],
  [Inbox, "Fatture ricevute", "Import di file XML e P7M dei fornitori, con creazione automatica dell'anagrafica."],
  [Undo2, "Note di credito", "Storno in un clic con riferimento alla fattura originale e sezionale dedicato."],
  [Hash, "Numerazione automatica", "Progressivi per anno e per sezionale, prefissi personalizzabili."],
  [FileDown, "PDF di cortesia", "Copia leggibile della fattura da inviare al cliente."],
  [LineChart, "Incassi e scadenze", "Dashboard con fatturato, IVA, crediti scaduti e pagamenti ai fornitori."],
  [Users, "Multi utente", "Ruoli Amministratore, Operatore e Sola lettura per ogni studio e azienda."],
  [Truck, "Documenti di trasporto", "DDT numerati da richiamare in fattura, con stampa PDF."],
  [Warehouse, "Magazzino fiscale", "Carichi e scarichi automatici, inventario, scorte minime e ordini ai fornitori."],
  [Receipt, "Corrispettivi e cassa", "Vendite al banco con lettore di codici a barre, chiusure giornaliere e registro corrispettivi."],
  [Printer, "Stampe PDF", "Elenchi, registri, schede articolo ed etichette con codice a barre pronti da stampare."],
];

const ROLES = [
  [Crown, "Studio Principale", "Crea gli studi, le aziende clienti e i pacchetti XML. Assegna a ogni azienda il suo spazio e il suo studio."],
  [Briefcase, "Studio commercialista", "Vede solo le aziende abbinate e scarica gli XML emessi e ricevuti, uno per uno o in un unico ZIP."],
  [Building2, "Azienda cliente", "Un gestionale completo: anagrafiche, articoli, fatture, invio SdI, fatture passive e scadenzario."],
];

export default function Landing() {
  const { user } = useAuth();
  const { hash } = useLocation();
  const { logo } = useBranding();

  useEffect(() => {
    if (hash) document.getElementById(hash.slice(1))?.scrollIntoView();
  }, [hash]);

  return (
    <div className="min-h-screen overflow-x-clip bg-slate-100">
      <header className="sticky top-0 z-30 border-b border-[#1E293B] bg-[#020617] shadow-lg">
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between gap-4 px-5 sm:h-24 sm:px-8">
          <Link to="/" data-testid="landing-home-logo" className="min-w-0"><BrandMark logo={logo} className="!max-h-12 sm:!max-h-16 !max-w-[220px] sm:!max-w-[420px]" /></Link>
          <nav className="hidden items-center gap-8 text-sm font-medium text-white lg:flex">
            <a href="#funzioni" data-testid="nav-features-link" className="transition-colors hover:text-sky-300">Funzioni</a>
            <a href="#prezzi" data-testid="nav-prices-link" className="transition-colors hover:text-sky-300">Prezzi</a>
            <a href="#ruoli" data-testid="nav-how-link" className="transition-colors hover:text-sky-300">Come funziona</a>
            <a href="#richiesta" data-testid="nav-request-link" className="transition-colors hover:text-sky-300">Richiedi un account</a>
            <Link to="/partner" data-testid="nav-partner-link" className="transition-colors hover:text-sky-300">Commercialisti</Link>
            <Link to="/prova" data-testid="nav-trial-link" className="rounded-md bg-sky-500 px-3 py-1.5 font-semibold text-[#020617] transition-colors hover:bg-sky-400">Prova gratis</Link>
          </nav>
          {user ? (
            <Button asChild size="sm" data-testid="landing-go-panel" className="shrink-0 bg-white text-[#020617] hover:bg-slate-100"><Link to={homeFor(user)}>Vai al pannello</Link></Button>
          ) : (
            <Button asChild size="sm" data-testid="landing-login-button" className="shrink-0 bg-white text-[#020617] shadow-sm hover:bg-slate-100"><Link to="/login">Accedi</Link></Button>
          )}
        </div>
      </header>

      <section className="relative overflow-hidden border-b border-slate-200 bg-gradient-to-b from-slate-200/70 via-slate-100 to-slate-100 text-slate-900">
        <div className="relative mx-auto grid max-w-7xl gap-16 px-5 pb-28 pt-20 sm:px-8 lg:grid-cols-[1.15fr_1fr] lg:pt-28">
          <div className="fade-up">
            <div className="mb-6 font-mono text-xs uppercase tracking-[0.3em] text-blue-700">Gestionale e fatturazione · Multi studio · Multi utente</div>
            <h1 className="font-display text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
              Gestionale e Fatturazione, <span className="text-blue-600">archivio XML per gli studi.</span>
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-600">
              Le aziende emettono e ricevono le fatture, gestiscono magazzino, DDT e corrispettivi. Gli studi commercialisti scaricano gli XML dei propri clienti. Lo studio principale gestisce tutto con i pacchetti XML.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Button asChild size="lg" data-testid="hero-request-account" className="h-12 bg-blue-600 px-6 shadow-sm hover:bg-blue-700"><Link to="/prova">Prova gratis 30 giorni <ArrowRight className="ml-2 h-4 w-4" /></Link></Button>
              <Button asChild size="lg" variant="outline" data-testid="hero-request-contact" className="h-12 bg-white px-6"><a href="#richiesta">Richiedi un contatto</a></Button>
            </div>
            <div className="mt-14 grid max-w-lg grid-cols-3 gap-6 border-t border-slate-200 pt-6 font-mono text-xs text-slate-500">
              <div><div className="text-lg text-slate-900">FPR12</div>B2B e PA</div>
              <div><div className="text-lg text-slate-900">TD01·TD04</div>Fatture e NC</div>
              <div><div className="text-lg text-slate-900">ZIP</div>Export per studio</div>
            </div>
          </div>
          <div className="mx-auto w-full max-w-xl lg:mt-6"><HeroSlideshow /></div>
        </div>
      </section>

      <ForfettariPromo />
      <PackagesSection />

      <section id="ruoli" className="mx-auto max-w-7xl px-5 py-24 sm:px-8">
        <div className="max-w-2xl">
          <div className="font-mono text-xs uppercase tracking-[0.25em] text-sky-700">Come funziona</div>
          <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">Tre livelli, un'unica piattaforma</h2>
        </div>
        <div className="stagger mt-12 grid gap-px overflow-hidden rounded-2xl border border-slate-200 bg-slate-200 md:grid-cols-3">
          {ROLES.map(([Icon, t, d], i) => (
            <div key={t} className="bg-white p-8">
              <div className="flex items-center justify-between">
                <Icon className="h-6 w-6 text-slate-900" strokeWidth={1.75} />
                <span className="font-mono text-xs text-slate-600">0{i + 1}</span>
              </div>
              <h3 className="mt-6 font-display text-lg font-bold text-slate-900">{t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{d}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="funzioni" className="border-y border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8">
          <div className="grid gap-12 lg:grid-cols-[1fr_2fr]">
            <div>
              <div className="font-mono text-xs uppercase tracking-[0.25em] text-sky-700">Funzioni</div>
              <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">Gestionale completo: fatture, magazzino e cassa</h2>
              <p className="mt-4 text-sm leading-relaxed text-slate-600">Dalla fattura al magazzino, dalla cassa all'archivio dello studio.</p>
            </div>
            <div className="grid gap-x-10 gap-y-10 sm:grid-cols-2">
              {FEATURES.map(([Icon, t, d]) => (
                <div key={t} className="flex gap-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-sky-50"><Icon className="h-5 w-5 text-sky-700" strokeWidth={1.75} /></div>
                  <div>
                    <h3 className="font-display text-base font-bold text-slate-900">{t}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-slate-600">{d}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section id="richiesta" className="border-t border-slate-200 bg-white">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 py-24 sm:px-8 lg:grid-cols-[1fr_1.4fr]">
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.25em] text-sky-700">Attivazione</div>
            <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">Richiedi un account</h2>
            <p className="mt-4 text-sm leading-relaxed text-slate-600">Sei uno studio commercialista o un'azienda e vuoi usare la piattaforma? Lascia i tuoi dati: ti ricontattiamo per attivare l'accesso e scegliere il pacchetto XML più adatto.</p>
            <ul className="mt-8 space-y-3 text-sm text-slate-700">
              {["Attivazione da parte dello Studio Principale", "Abbinamento al tuo studio di riferimento", "Pacchetto XML in base ai tuoi volumi"].map((t) => (
                <li key={t} className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-emerald-600" />{t}</li>
              ))}
            </ul>
          </div>
          <RequestForm />
        </div>
      </section>

      <section className="border-t border-slate-300 bg-slate-200/70">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-6 px-5 py-16 sm:px-8 md:flex-row md:items-center">
          <div>
            <h2 className="font-display text-2xl font-extrabold text-slate-900 sm:text-3xl">Hai già un account?</h2>
            <p className="mt-2 text-sm text-slate-600">Gli accessi vengono creati dallo studio principale o dal tuo studio.</p>
          </div>
          <Button asChild size="lg" data-testid="cta-login" className="h-12 bg-blue-600 px-6 shadow-sm hover:bg-blue-700"><Link to="/login"><Download className="mr-2 h-4 w-4 rotate-[-90deg]" />Accedi alla piattaforma</Link></Button>
        </div>
      </section>

      <footer data-testid="site-footer" className="border-t border-[#1E293B] bg-[#020617] text-white">
        <div className="mx-auto grid max-w-7xl gap-10 px-5 py-12 text-sm sm:px-8 md:grid-cols-3">
          <div>
            <BrandMark logo={logo} className="!max-h-12 !max-w-[320px]" />
            <p className="mt-3 max-w-xs text-slate-200">Gestionale e fatturazione elettronica per aziende e studi: magazzino fiscale, DDT, stampe PDF e invio SdI, conforme FatturaPA v1.2.2.</p>
          </div>
          <address data-testid="footer-company-legal-info" className="space-y-1 not-italic leading-relaxed text-slate-200">
            <div className="font-semibold text-white">Generservice Srl Semplificata</div>
            <div>Traversa di Vittorio Bachelet 7</div>
            <div>70010 Adelfia (BA)</div>
            <div>Tel. <a href="tel:3476172250" data-testid="footer-phone-link" className="hover:text-sky-300">3476172250</a></div>
            <div>Email <a href="mailto:clienti@fatturazionegestionale.com" data-testid="footer-email-link" className="hover:text-sky-300">clienti@fatturazionegestionale.com</a></div>
            <div>PEC <a href="mailto:generservicesrl@pec.it" data-testid="footer-pec-link" className="hover:text-sky-300">generservicesrl@pec.it</a></div>
            <div>Partita IVA 07680350720</div>
          </address>
          <nav className="flex flex-col gap-2 text-slate-200 md:items-end">
            <a href="#funzioni" data-testid="footer-features-link" className="hover:text-sky-300">Funzioni</a>
            <a href="#richiesta" data-testid="footer-request-link" className="hover:text-sky-300">Richiedi un account</a>
            <Link to="/prova" data-testid="footer-trial-link" className="hover:text-sky-300">Prova gratuita</Link>
            <Link to="/partner" data-testid="footer-partner-link" className="hover:text-sky-300">Diventa partner</Link>
            {Object.entries(FUNZIONI).map(([k, v]) => <Link key={k} to={`/funzioni/${k}`} data-testid={`footer-feature-${k}`} className="hover:text-sky-300">{v[1]}</Link>)}
            <Link to="/login" data-testid="footer-login-link" className="hover:text-sky-300">Accedi</Link>
            <Link to="/privacy" data-testid="footer-privacy-link" className="hover:text-sky-300">Privacy</Link>
            <Link to="/cookie" data-testid="footer-cookie-link" className="hover:text-sky-300">Cookie policy</Link>
            <Link to="/termini" data-testid="footer-terms-link" className="hover:text-sky-300">Termini di servizio</Link>
          </nav>
        </div>
        <div className="border-t border-[#1E293B]">
          <div className="mx-auto flex max-w-7xl flex-col justify-between gap-1 px-5 py-4 text-xs text-slate-300 sm:flex-row sm:px-8">
            <span>© {new Date().getFullYear()} Generservice Srl Semplificata · P.IVA 07680350720</span>
            <span className="font-mono">FatturaPA v1.2.2 · SdI</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
